/**
 * Playout as part of a config (docs/wiring-documentation-proposal.md,
 * "Playout"). `config_data.playout` holds what used to live only in the
 * bridge's SQLite: settings, assets, playouts, playlists with items and
 * schedules. The bridge keeps its SQLite tables as the working copy, written
 * in the load transaction and exported with every push. Live run state
 * (runs, fired triggers) stays on the bridge; what played goes to the cloud's
 * append-only `playout_run_logs`.
 *
 * | Bridge before                                  | Config                                     |
 * | ---------------------------------------------- | ------------------------------------------ |
 * | `playout_config.connection_id`                 | `settings.atem_device_id` (device ID)      |
 * | `playout_config.vmix_connection_id`            | `settings.vmix_device_id` (device ID)      |
 * | `playout_config.cutin_atem_input`              | `settings.cutin_port_id` (port ID)         |
 * | me_index, ssrc_id, program_source, media_player, timeouts | `settings.*`                    |
 * | `counters_reset_at`                            | `settings.counters_reset_at`               |
 * | `playout_assets` (+ media slot)                | `assets` + `media_pool` (`use: "playout"`) |
 * | playouts, playlists, playlist_items, triggers  | `playouts`, `playlists[].items/triggers`   |
 *
 * The helpers below edit a config's playout the way the bridge routes edit
 * its tables, for the cloud config editor (inactive configs only: the active
 * one is locked and edited live on the bridge).
 */
import { DEFAULT_SQUEEZE_MS } from "../animation-duration.js";
import { allocateStillSlot, DEFAULT_STILL_POOL_SIZE, findPlayoutSlot, stillPoolSizeFromState, } from "./media.js";
export const DEFAULT_CONFIG_PLAYOUT_SETTINGS = {
    atem_device_id: null,
    vmix_device_id: null,
    cutin_port_id: null,
    me_index: 1, // ME2
    ssrc_id: 0,
    program_source: 0,
    media_player: 0,
    vmix_ready_timeout_ms: 5000,
    counters_reset_at: null,
};
export function emptyConfigPlayout() {
    return {
        settings: { ...DEFAULT_CONFIG_PLAYOUT_SETTINGS },
        assets: [],
        playouts: [],
        playlists: [],
    };
}
export function configPlayoutOf(data) {
    const playout = data?.playout;
    if (!playout)
        return emptyConfigPlayout();
    return {
        settings: { ...DEFAULT_CONFIG_PLAYOUT_SETTINGS, ...playout.settings },
        assets: playout.assets ?? [],
        playouts: playout.playouts ?? [],
        playlists: (playout.playlists ?? []).map((playlist) => ({
            ...playlist,
            items: playlist.items ?? [],
            triggers: playlist.triggers ?? [],
        })),
    };
}
/** ATEM input ID behind a port (its control address), or null. */
export function atemInputIDForPort(ports, portID) {
    if (!portID)
        return null;
    const port = ports.find((candidate) => candidate.id === portID);
    const control = port?.control;
    return control?.kind === "atem_input" ? control.input_id : null;
}
/** The input port of an ATEM whose control address is `inputID`, or null. */
export function atemInputPortID(ports, deviceID, inputID) {
    if (!deviceID)
        return null;
    const port = ports.find((candidate) => candidate.deviceID === deviceID &&
        candidate.direction === "input" &&
        candidate.control?.kind === "atem_input" &&
        candidate.control.input_id === inputID);
    return port?.id ?? null;
}
// ─── Editing (cloud config editor) ─────────────────────────────
export class ConfigPlayoutError extends Error {
}
const defaultClock = {
    id: () => crypto.randomUUID(),
    now: () => new Date().toISOString(),
};
function withPlayout(data, playout, mediaPool) {
    return {
        ...data,
        playout,
        ...(mediaPool !== undefined ? { media_pool: mediaPool } : {}),
    };
}
/** Pool size of an ATEM as far as the config knows it. */
export function configStillPoolSize(data, deviceID) {
    return (stillPoolSizeFromState(data.device_states?.[deviceID]?.values) ??
        DEFAULT_STILL_POOL_SIZE);
}
/**
 * Give every still playout asset without a slot one on the playout ATEM
 * (and drop slots of assets that are gone). Assets can't get a slot while no
 * ATEM is selected; the editor shows them as "no slot".
 */
export function placePlayoutStills(data) {
    const playout = configPlayoutOf(data);
    const deviceID = playout.settings.atem_device_id;
    const pools = {};
    const assetIDs = new Set(playout.assets.map((asset) => asset.id));
    // Drop playout slots of deleted assets and of other ATEMs
    for (const [poolDevice, pool] of Object.entries(data.media_pool ?? {})) {
        const stills = {};
        for (const [index, slot] of Object.entries(pool.stills ?? {})) {
            if (slot.use === "playout" &&
                (!slot.playout_asset_id ||
                    !assetIDs.has(slot.playout_asset_id) ||
                    poolDevice !== deviceID)) {
                continue;
            }
            stills[index] = slot;
        }
        pools[poolDevice] = { ...pool, stills };
    }
    if (deviceID) {
        const pool = pools[deviceID] ?? { stills: {} };
        const size = configStillPoolSize(data, deviceID);
        for (const asset of playout.assets) {
            if (asset.media_type === "video" || !asset.media)
                continue;
            const existing = findPlayoutSlot(pool, asset.id);
            if (existing !== null) {
                pool.stills[String(existing)] = {
                    ...pool.stills[String(existing)],
                    asset: asset.media,
                    name: asset.name,
                };
                continue;
            }
            const index = allocateStillSlot(pool, size);
            if (index === null) {
                throw new ConfigPlayoutError(`No free media pool slot for "${asset.name}" (all ${size} are used)`);
            }
            pool.stills[String(index)] = {
                asset: asset.media,
                name: asset.name,
                use: "playout",
                playout_asset_id: asset.id,
            };
        }
        pools[deviceID] = pool;
    }
    return { ...data, media_pool: pools };
}
export function updateConfigPlayoutSettings(data, patch) {
    const playout = configPlayoutOf(data);
    return placePlayoutStills(withPlayout(data, {
        ...playout,
        settings: { ...playout.settings, ...patch },
    }));
}
export function addConfigPlayoutAsset(data, input, clock = defaultClock) {
    const playout = configPlayoutOf(data);
    const mediaType = input.media_type ?? "still";
    if (mediaType === "still" && !input.media) {
        throw new ConfigPlayoutError("A still needs its artwork in studio media");
    }
    const asset = {
        id: clock.id(),
        name: input.name,
        media_type: mediaType,
        image_url: input.image_url,
        ...(input.media ? { media: input.media } : {}),
        width: input.width,
        height: input.height,
        ...(input.vmix_input != null ? { vmix_input: input.vmix_input } : {}),
        ...(input.duration_seconds != null
            ? { duration_seconds: input.duration_seconds }
            : {}),
        ...(input.hold_override_seconds != null
            ? { hold_override_seconds: input.hold_override_seconds }
            : {}),
        created_at: clock.now(),
    };
    return {
        data: placePlayoutStills(withPlayout(data, { ...playout, assets: [...playout.assets, asset] })),
        asset,
    };
}
/** `null` clears an optional field, `undefined` leaves it. */
export function updateConfigPlayoutAsset(data, id, patch) {
    const playout = configPlayoutOf(data);
    if (!playout.assets.some((asset) => asset.id === id)) {
        throw new ConfigPlayoutError("Asset not found");
    }
    const assets = playout.assets.map((asset) => {
        if (asset.id !== id)
            return asset;
        const next = { ...asset };
        if (patch.name !== undefined)
            next.name = patch.name;
        for (const key of [
            "vmix_input",
            "duration_seconds",
            "hold_override_seconds",
        ]) {
            const value = patch[key];
            if (value === null)
                delete next[key];
            else if (value !== undefined)
                next[key] = value;
        }
        return next;
    });
    return placePlayoutStills(withPlayout(data, { ...playout, assets }));
}
export function deleteConfigPlayoutAsset(data, id) {
    const playout = configPlayoutOf(data);
    if (playout.playouts.some((item) => item.asset_id === id)) {
        throw new ConfigPlayoutError("Asset is used by a playout and cannot be deleted");
    }
    return placePlayoutStills(withPlayout(data, {
        ...playout,
        assets: playout.assets.filter((asset) => asset.id !== id),
    }));
}
export function addConfigPlayout(data, input, clock = defaultClock) {
    const playout = configPlayoutOf(data);
    const asset = playout.assets.find((item) => item.id === input.asset_id);
    if (!asset)
        throw new ConfigPlayoutError("Asset not found");
    // The media decides the type, as on the bridge
    const type = asset.media_type === "video" ? "video-cutin" : "l-shape";
    if (input.playout_type && input.playout_type !== type) {
        throw new ConfigPlayoutError(`"${asset.name}" is a ${asset.media_type}, so this playout must be a ${type}`);
    }
    const now = clock.now();
    const created = {
        id: clock.id(),
        name: input.name,
        playout_type: type,
        asset_id: input.asset_id,
        x: input.x,
        y: input.y,
        size: input.size,
        in_duration_ms: input.in_duration_ms ?? DEFAULT_SQUEEZE_MS,
        out_duration_ms: input.out_duration_ms ?? DEFAULT_SQUEEZE_MS,
        easing: input.easing ?? "ease-in-out",
        border_enabled: input.border_enabled ?? false,
        ...(input.border_color ? { border_color: input.border_color } : {}),
        ...(input.border_width != null ? { border_width: input.border_width } : {}),
        created_at: now,
        updated_at: now,
    };
    return {
        data: withPlayout(data, {
            ...playout,
            playouts: [...playout.playouts, created],
        }),
        playout: created,
    };
}
export function updateConfigPlayout(data, id, patch, clock = defaultClock) {
    const playout = configPlayoutOf(data);
    if (!playout.playouts.some((item) => item.id === id)) {
        throw new ConfigPlayoutError("Playout not found");
    }
    const { playout_type: _ignored, ...rest } = patch;
    return withPlayout(data, {
        ...playout,
        playouts: playout.playouts.map((item) => item.id === id ? { ...item, ...rest, updated_at: clock.now() } : item),
    });
}
export function deleteConfigPlayout(data, id) {
    const playout = configPlayoutOf(data);
    // Items playing it go with it (ON DELETE CASCADE on the bridge)
    return withPlayout(data, {
        ...playout,
        playouts: playout.playouts.filter((item) => item.id !== id),
        playlists: playout.playlists.map((playlist) => ({
            ...playlist,
            items: playlist.items.filter((item) => item.playout_id !== id),
        })),
    });
}
export function addConfigPlaylist(data, input, clock = defaultClock) {
    const playout = configPlayoutOf(data);
    const now = clock.now();
    const playlist = {
        id: clock.id(),
        name: input.name,
        ...(input.description ? { description: input.description } : {}),
        loop_enabled: input.loop_enabled ?? false,
        ...(input.loop_seconds != null ? { loop_seconds: input.loop_seconds } : {}),
        created_at: now,
        updated_at: now,
        items: [],
        triggers: [],
    };
    return {
        data: withPlayout(data, {
            ...playout,
            playlists: [...playout.playlists, playlist],
        }),
        playlist,
    };
}
function updatePlaylists(data, playlistID, update) {
    const playout = configPlayoutOf(data);
    if (!playout.playlists.some((playlist) => playlist.id === playlistID)) {
        throw new ConfigPlayoutError("Playlist not found");
    }
    return withPlayout(data, {
        ...playout,
        playlists: playout.playlists.map((playlist) => playlist.id === playlistID ? update(playlist) : playlist),
    });
}
function playlistOf(data, predicate) {
    return configPlayoutOf(data).playlists.find(predicate) ?? null;
}
export function updateConfigPlaylist(data, id, patch, clock = defaultClock) {
    return updatePlaylists(data, id, (playlist) => {
        const next = { ...playlist, updated_at: clock.now() };
        if (patch.name !== undefined)
            next.name = patch.name;
        if (patch.description !== undefined)
            next.description = patch.description;
        if (patch.loop_enabled !== undefined)
            next.loop_enabled = patch.loop_enabled;
        if (patch.loop_seconds === null)
            delete next.loop_seconds;
        else if (patch.loop_seconds !== undefined)
            next.loop_seconds = patch.loop_seconds;
        return next;
    });
}
export function deleteConfigPlaylist(data, id) {
    const playout = configPlayoutOf(data);
    return withPlayout(data, {
        ...playout,
        playlists: playout.playlists.filter((playlist) => playlist.id !== id),
    });
}
export function addConfigPlaylistItem(data, playlistID, input, clock = defaultClock) {
    if (!configPlayoutOf(data).playouts.some((p) => p.id === input.playout_id)) {
        throw new ConfigPlayoutError("Playout not found");
    }
    let created = null;
    const next = updatePlaylists(data, playlistID, (playlist) => {
        created = {
            id: clock.id(),
            playlist_id: playlistID,
            playout_id: input.playout_id,
            position: input.position ??
                playlist.items.reduce((max, item) => Math.max(max, item.position + 1), 0),
            offset_seconds: input.offset_seconds,
            ...(input.hold_seconds != null
                ? { hold_seconds: input.hold_seconds }
                : {}),
            enabled: input.enabled ?? true,
        };
        return { ...playlist, items: [...playlist.items, created] };
    });
    return { data: next, item: created };
}
export function updateConfigPlaylistItem(data, itemID, patch) {
    const playlist = playlistOf(data, (p) => p.items.some((item) => item.id === itemID));
    if (!playlist)
        throw new ConfigPlayoutError("Item not found");
    return updatePlaylists(data, playlist.id, (current) => ({
        ...current,
        items: current.items.map((item) => {
            if (item.id !== itemID)
                return item;
            const next = { ...item };
            if (patch.playout_id !== undefined)
                next.playout_id = patch.playout_id;
            if (patch.position !== undefined)
                next.position = patch.position;
            if (patch.offset_seconds !== undefined)
                next.offset_seconds = patch.offset_seconds;
            if (patch.enabled !== undefined)
                next.enabled = patch.enabled;
            if (patch.hold_seconds === null)
                delete next.hold_seconds;
            else if (patch.hold_seconds !== undefined)
                next.hold_seconds = patch.hold_seconds;
            return next;
        }),
    }));
}
export function deleteConfigPlaylistItem(data, itemID) {
    const playlist = playlistOf(data, (p) => p.items.some((item) => item.id === itemID));
    if (!playlist)
        throw new ConfigPlayoutError("Item not found");
    return updatePlaylists(data, playlist.id, (current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== itemID),
    }));
}
export function addConfigPlaylistTrigger(data, playlistID, input, clock = defaultClock) {
    let created = null;
    const next = updatePlaylists(data, playlistID, (playlist) => {
        created = {
            id: clock.id(),
            playlist_id: playlistID,
            starts_at: input.starts_at,
            timezone: input.timezone ?? "UTC",
            enabled: input.enabled ?? true,
        };
        return { ...playlist, triggers: [...playlist.triggers, created] };
    });
    return { data: next, trigger: created };
}
export function updateConfigPlaylistTrigger(data, triggerID, patch) {
    const playlist = playlistOf(data, (p) => p.triggers.some((trigger) => trigger.id === triggerID));
    if (!playlist)
        throw new ConfigPlayoutError("Trigger not found");
    return updatePlaylists(data, playlist.id, (current) => ({
        ...current,
        triggers: current.triggers.map((trigger) => trigger.id === triggerID ? { ...trigger, ...patch } : trigger),
    }));
}
export function deleteConfigPlaylistTrigger(data, triggerID) {
    const playlist = playlistOf(data, (p) => p.triggers.some((trigger) => trigger.id === triggerID));
    if (!playlist)
        throw new ConfigPlayoutError("Trigger not found");
    return updatePlaylists(data, playlist.id, (current) => ({
        ...current,
        triggers: current.triggers.filter((trigger) => trigger.id !== triggerID),
    }));
}
// ─── Copies ────────────────────────────────────────────────────
/**
 * Rewrite device IDs of the media pool and the playout settings for a copy
 * (same rule as `device_states`: unmapped devices keep their ID when
 * `keepUnmapped`, else they are dropped).
 */
export function rewriteMediaAndPlayoutDevices(data, deviceMap, keepUnmapped) {
    const map = (id) => {
        if (!id)
            return null;
        const mapped = deviceMap?.get(id);
        if (mapped !== undefined)
            return mapped;
        return keepUnmapped ? id : null;
    };
    let next = data;
    if (data.media_pool) {
        const pools = {};
        for (const [deviceID, pool] of Object.entries(data.media_pool)) {
            const target = map(deviceID);
            if (target)
                pools[target] = pool;
        }
        next = { ...next, media_pool: pools };
    }
    if (data.playout) {
        const playout = configPlayoutOf(data);
        next = {
            ...next,
            playout: {
                ...playout,
                settings: {
                    ...playout.settings,
                    atem_device_id: map(playout.settings.atem_device_id),
                    vmix_device_id: map(playout.settings.vmix_device_id),
                },
            },
        };
    }
    return next;
}
